import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import { usePermissions, Role, Module } from '@/contexts/PermissionContext';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  CardFooter
} from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Shield, UserPlus, Pencil, Trash, Check, X, Plus, Save, Users } from 'lucide-react';

// Interface para usuários
interface User {
  id: string;
  email: string;
  created_at: string;
  user_roles: {
    role: Role;
  }[];
}

// Interface para permissões
interface Permission {
  id: string;
  module_id: string;
  action: string;
  description: string;
}

// Interface para associação de permissões a papéis
interface RolePermission {
  role_id: string;
  permission_id: string;
}

const AccessControl = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { userRoles, refreshPermissions } = usePermissions();
  
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [userRoleId, setUserRoleId] = useState<string | null>(null);
  
  // Verificar se o usuário atual é admin
  const isAdmin = userRoles.some(role => 
    role.name.toLowerCase() === 'admin'
  );
  
  // Buscar dados iniciais
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Buscar usuários básicos, sem papéis
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, email, created_at');
        
        if (usersError) throw usersError;
        
        // Buscar papéis dos usuários em uma consulta separada
        const { data: userRolesData, error: userRolesError } = await supabase
          .from('user_roles')
          .select('user_id, role_id');
        
        if (userRolesError) throw userRolesError;
        
        // Buscar papéis
        const { data: rolesData, error: rolesError } = await supabase
          .from('roles')
          .select('*')
          .order('name');
        
        if (rolesError) throw rolesError;
        
        // Transformar os dados para o formato esperado
        const formattedUsers: User[] = (usersData || []).map(userData => {
          // Obter os papéis deste usuário
          const userRoleIds = userRolesData
            ?.filter(ur => ur.user_id === userData.id)
            .map(ur => ur.role_id) || [];
          
          const userRoles = userRoleIds.map(roleId => {
            const role = rolesData?.find(r => r.id === roleId);
            return { role: role as Role };
          }).filter(ur => ur.role); // Filtrar papéis não encontrados
          
          return {
            id: userData.id,
            email: userData.email,
            created_at: userData.created_at,
            user_roles: userRoles
          };
        });
        
        // Buscar módulos
        const { data: modulesData, error: modulesError } = await supabase
          .rpc('get_all_modules');
        
        if (modulesError) throw modulesError;
        
        // Buscar permissões
        const { data: permissionsData, error: permissionsError } = await supabase
          .from('permissions')
          .select('*');
        
        if (permissionsError) throw permissionsError;
        
        // Buscar associações de permissões a papéis
        const { data: rolePermissionsData, error: rolePermissionsError } = await supabase
          .from('role_permissions')
          .select('*');
        
        if (rolePermissionsError) throw rolePermissionsError;
        
        // Atualizar estados
        setUsers(formattedUsers);
        setRoles(rolesData || []);
        setModules(modulesData || []);
        setPermissions(permissionsData || []);
        setRolePermissions(rolePermissionsData || []);
        
        // Selecionar o primeiro papel por padrão
        if (rolesData && rolesData.length > 0) {
          setSelectedRole(rolesData[0].id);
        }
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        toast.error('Erro ao carregar dados de controle de acesso');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Verificar se uma permissão está associada ao papel selecionado
  const hasPermission = (permissionId: string): boolean => {
    if (!selectedRole) return false;
    
    return rolePermissions.some(
      rp => rp.role_id === selectedRole && rp.permission_id === permissionId
    );
  };
  
  // Alternar permissão para o papel selecionado
  const togglePermission = async (permissionId: string) => {
    if (!selectedRole) return;
    
    try {
      const hasPermissionAlready = hasPermission(permissionId);
      
      if (hasPermissionAlready) {
        // Remover permissão (DELETE não tem problemas com triggers)
        const { error } = await supabase
          .from('role_permissions')
          .delete()
          .eq('role_id', selectedRole)
          .eq('permission_id', permissionId);
        
        if (error) throw error;
        
        // Atualizar estado local
        setRolePermissions(rolePermissions.filter(
          rp => !(rp.role_id === selectedRole && rp.permission_id === permissionId)
        ));
        
        toast.success('Permissão removida com sucesso');
      } else {
        // Adicionar permissão usando a nova função RPC
        const { error } = await supabase
          .rpc('insert_role_permission', {
            p_role_id: selectedRole,
            p_permission_id: permissionId
          });
        
        if (error) throw error;
        
        // Atualizar estado local
        setRolePermissions([
          ...rolePermissions,
          { role_id: selectedRole, permission_id: permissionId }
        ]);
        
        toast.success('Permissão adicionada com sucesso');
      }
      
      // Atualizar permissões no contexto
      await refreshPermissions();
    } catch (error) {
      console.error('Erro ao alternar permissão:', error);
      toast.error('Erro ao alternar permissão');
    }
  };
  
  // Criar novo papel
  const createRole = async () => {
    if (!newRoleName.trim()) {
      toast.error('Nome do papel é obrigatório');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('roles')
        .insert({
          name: newRoleName.trim(),
          description: newRoleDescription.trim() || null,
          is_system: false
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Atualizar estado local
      setRoles([...roles, data]);
      setSelectedRole(data.id);
      
      // Limpar formulário
      setNewRoleName('');
      setNewRoleDescription('');
      setIsRoleDialogOpen(false);
      
      toast.success('Papel criado com sucesso');
    } catch (error) {
      console.error('Erro ao criar papel:', error);
      toast.error('Erro ao criar papel');
    }
  };
  
  // Atribuir papel a usuário
  const assignRoleToUser = async () => {
    if (!selectedUser || !userRoleId) {
      toast.error('Selecione um usuário e um papel');
      return;
    }
    
    try {
      // Verificar se o usuário já tem esse papel
      const userHasRole = selectedUser.user_roles.some(
        ur => ur.role.id === userRoleId
      );
      
      if (userHasRole) {
        toast.error('Usuário já possui esse papel');
        return;
      }
      
      // Adicionar papel ao usuário
      const { error } = await supabase
        .from('user_roles')
        .insert({
          user_id: selectedUser.id,
          role_id: userRoleId
        });
      
      if (error) throw error;
      
      // Atualizar estado local
      const role = roles.find(r => r.id === userRoleId);
      if (role) {
        const updatedUsers = users.map(u => {
          if (u.id === selectedUser.id) {
            return {
              ...u,
              user_roles: [...u.user_roles, { role }]
            };
          }
          return u;
        });
        
        setUsers(updatedUsers);
        setSelectedUser(null);
        setUserRoleId(null);
        setIsUserDialogOpen(false);
      }
      
      // Atualizar permissões no contexto
      await refreshPermissions();
      
      toast.success('Papel atribuído com sucesso');
    } catch (error) {
      console.error('Erro ao atribuir papel:', error);
      toast.error('Erro ao atribuir papel ao usuário');
    }
  };
  
  // Remover papel de usuário
  const removeRoleFromUser = async (userId: string, roleId: string) => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role_id', roleId);
      
      if (error) throw error;
      
      // Atualizar estado local
      const updatedUsers = users.map(u => {
        if (u.id === userId) {
          return {
            ...u,
            user_roles: u.user_roles.filter(ur => ur.role.id !== roleId)
          };
        }
        return u;
      });
      
      setUsers(updatedUsers);
      
      // Atualizar permissões no contexto
      await refreshPermissions();
      
      toast.success('Papel removido com sucesso');
    } catch (error) {
      console.error('Erro ao remover papel:', error);
      toast.error('Erro ao remover papel do usuário');
    }
  };
  
  // Obter módulos filhos
  const getChildModules = (parentId: string | null): Module[] => {
    return modules.filter(m => m.parent_id === parentId);
  };
  
  // Obter permissões de um módulo
  const getModulePermissions = (moduleId: string): Permission[] => {
    return permissions.filter(p => p.module_id === moduleId);
  };
  
  // Renderizar tabela de permissões para módulos
  const renderModulePermissions = (parentId: string | null = null, level: number = 0) => {
    const childModules = getChildModules(parentId);
    
    if (childModules.length === 0) return null;
    
    return (
      <>
        {childModules.map(module => (
          <React.Fragment key={module.id}>
            <TableRow className={level === 0 ? 'bg-muted/50' : ''}>
              <TableCell 
                colSpan={5} 
                className={`font-extrabold ${level > 0 ? 'pl-' + (level * 4 + 4) : ''}`}
              >
                {module.name}
              </TableCell>
            </TableRow>
            
            {getModulePermissions(module.id).map(permission => (
              <TableRow key={permission.id}>
                <TableCell className={`pl-${level * 4 + 8}`}>
                  <div className="flex items-center space-x-2">
                    <div className="w-4"></div>
                    <span>{permission.action}</span>
                  </div>
                </TableCell>
                <TableCell>{permission.description}</TableCell>
                <TableCell className="text-center">
                  <Checkbox 
                    checked={hasPermission(permission.id)}
                    onCheckedChange={() => togglePermission(permission.id)}
                    disabled={
                      !selectedRole || 
                      roles.find(r => r.id === selectedRole)?.is_system
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
            
            {renderModulePermissions(module.id, level + 1)}
          </React.Fragment>
        ))}
      </>
    );
  };
  
  // Se não for admin, redirecionar
  if (!isAdmin) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-extrabold">Controle de Acesso</h1>
            <p className="text-muted-foreground">
              Gerencie papéis e permissões de usuários no sistema
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsRoleDialogOpen(true)}>
              <Shield className="h-4 w-4 mr-2" />
              Nova Função
            </Button>
            <Button onClick={() => setIsUserDialogOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Atribuir Função
            </Button>
          </div>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Acesso Negado</CardTitle>
            <CardDescription>
              Você não tem permissão para acessar esta página.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p>Esta página é restrita a administradores do sistema.</p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/')}>Voltar para o Dashboard</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-extrabold">Controle de Acesso</h1>
          <p className="text-muted-foreground">
            Gerencie usuários, funções e permissões do sistema
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsRoleDialogOpen(true)}>
            <Shield className="h-4 w-4 mr-2" />
            Nova Função
          </Button>
          <Button onClick={() => setIsUserDialogOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Atribuir Função
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Gerenciamento de Acesso</CardTitle>
          <CardDescription>
            Configure perfis de acesso e gerencie permissões de usuários
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="roles" className="space-y-6">
            <TabsList className="grid grid-cols-2 w-full md:w-[400px]">
              <TabsTrigger value="roles">
                <Shield className="h-4 w-4 mr-2" />
                Funções e Permissões
              </TabsTrigger>
              <TabsTrigger value="users">
                <Users className="h-4 w-4 mr-2" />
                Usuários
              </TabsTrigger>
            </TabsList>

            <TabsContent value="roles" className="space-y-6">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div>
                </div>
              ) : (
                <div className="grid md:grid-cols-[250px_1fr] gap-6">
                  {/* Lista de funções */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Funções</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <ul className="divide-y">
                        {roles.map(role => (
                          <li 
                            key={role.id}
                            onClick={() => setSelectedRole(role.id)}
                            className={`px-4 py-2 cursor-pointer hover:bg-secondary/50 ${selectedRole === role.id ? 'bg-secondary' : ''}`}
                          >
                            {role.name}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Permissões da função selecionada */}
                  {selectedRole && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">
                          Permissões: {roles.find(r => r.id === selectedRole)?.name}
                        </CardTitle>
                        <CardDescription>
                          Selecione as permissões para esta função
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-auto max-h-[500px] pr-2">
                          <Table>
                            <TableBody>
                              {renderModulePermissions()}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="users">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Funções</TableHead>
                        <TableHead>Data de Criação</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-6">
                            Nenhum usuário encontrado.
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map(user => (
                          <TableRow key={user.id}>
                            <TableCell className="font-extrabold">{user.email}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {user.user_roles.map(ur => (
                                  <div 
                                    key={ur.role.id} 
                                    className="text-xs bg-primary/10 border border-primary/20 rounded px-2 py-1 flex items-center"
                                  >
                                    {ur.role.name}
                                    <button 
                                      onClick={() => removeRoleFromUser(user.id, ur.role.id)}
                                      className="ml-1 rounded-full hover:bg-secondary p-0.5"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              {new Date(user.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setIsUserDialogOpen(true);
                                }}
                              >
                                <UserPlus className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Dialog para atribuir função a usuário */}
      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atribuir Função a Usuário</DialogTitle>
            <DialogDescription>
              {selectedUser 
                ? `Atribuir nova função ao usuário ${selectedUser.email}` 
                : 'Selecione um usuário e uma função para atribuir'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!selectedUser && (
              <div className="space-y-2">
                <Label htmlFor="user">Usuário</Label>
                <Select onValueChange={(value) => {
                  const user = users.find(u => u.id === value) || null;
                  setSelectedUser(user);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um usuário" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="role">Função</Label>
              <Select onValueChange={setUserRoleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma função" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsUserDialogOpen(false);
              setSelectedUser(null);
              setUserRoleId(null);
            }}>
              Cancelar
            </Button>
            <Button 
              onClick={assignRoleToUser}
              disabled={!selectedUser || !userRoleId}
            >
              Atribuir Função
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog para criar nova função */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Nova Função</DialogTitle>
            <DialogDescription>
              Defina um nome e descrição para a nova função
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="role-name">Nome da Função</Label>
              <Input
                id="role-name"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="Ex: editor, approver, etc."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role-description">Descrição</Label>
              <Input
                id="role-description"
                value={newRoleDescription}
                onChange={(e) => setNewRoleDescription(e.target.value)}
                placeholder="Descreva a finalidade desta função"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsRoleDialogOpen(false);
              setNewRoleName('');
              setNewRoleDescription('');
            }}>
              Cancelar
            </Button>
            <Button 
              onClick={createRole}
              disabled={!newRoleName.trim()}
            >
              Criar Função
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessControl; 